import { useEffect } from "react";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface UseFilterResetProps {
  groupFilter: string;
  categoryFilter: string;
  categoryGroups: TransactionCategoryGroup[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  setGroupFilter: (value: string) => void;
  setCategoryFilter: (value: string) => void;
}

/**
 * Hook to reset filters when selected values are no longer available.
 * Resets group/category filters if the selected values don't exist anymore.
 */
export const useFilterReset = ({
  groupFilter,
  categoryFilter,
  categoryGroups,
  expenseCategories,
  incomeCategories,
  setGroupFilter,
  setCategoryFilter,
}: UseFilterResetProps): void => {
  // Reset filters if selected values are no longer available (only reset if truly invalid)
  // Note: Since we always include selected values in available options, these should rarely trigger
  useEffect(() => {
    // Reset group filter if current selection is not available (only if group doesn't exist at all)
    if (groupFilter !== "All") {
      const groupExists = categoryGroups.some((g) => g.name === groupFilter);
      if (!groupExists) {
        setGroupFilter("All");
        setCategoryFilter("All"); // Also reset category when group becomes invalid
      }
    }
  }, [groupFilter, categoryGroups, setGroupFilter, setCategoryFilter]);

  useEffect(() => {
    // Reset category filter if current selection is not available (only if category doesn't exist at all)
    if (categoryFilter !== "All") {
      const categoryExists = expenseCategories.some((cat) => cat.name === categoryFilter) ||
                            incomeCategories.some((cat) => cat.name === categoryFilter);
      if (!categoryExists) {
        setCategoryFilter("All");
      }
    }
  }, [categoryFilter, expenseCategories, incomeCategories, setCategoryFilter]);
};
